import { useRef, useEffect, useCallback, useState } from "react";
import { defineMessages } from "react-intl";
import classNames from "classnames";

import { useResponsiveFlag } from "../Utils/ResponsiveQueries";
import OptionInput from "./OptionInput";
import GaugeSlider from "./GaugeSlider";
import BudgetLineCheckboxInput from "./BudgetLineCheckboxInput";
import BudgetAmount from "../Form/Field/Budget/BudgetAmount";
import dispatchEvent from "../Utils/dispatchEvent";
import CollapseContent from "../Utils/CollapseContent";
import Button from "../Buttons/Button";
import { useUniqueId } from "../Utils/uniqueId";
import Tooltip from "../Tooltip/Tooltip";
import { useFormatMessage } from "../Intl/Context";
import RichText from "../Utils/RichText";

const DISPLAY_MODE_CHECK_BOXES = "check_boxes";

const BudgetLineInput = ({
  budgetLine,
  fieldName,
  isLineChecked,
  currentValue,
  currentOptionIds = [],
  unit,
  onUpdate
}) => {
  const { id, name, withOptions, displayMode, maximumSelectableOptions } = budgetLine;
  const containerRef = useRef();
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);

  const { isDesktop, isNonDesktop } = useResponsiveFlag();

  const t = useFormatMessage();
  const showDescriptionLabel = t(intlMessages.showDescription);

  const collapseId = useUniqueId("budget-line-description-");

  const toggleDescription = useCallback(() => {
    setIsDescriptionExpanded(prev => !prev);
  }, []);

  const handleOptionUpdate = useCallback(
    selectedOptions => {
      // For checkboxes, selectedOptions is an array of options
      const values = Array.isArray(selectedOptions) ? selectedOptions : [selectedOptions];

      const totalValue = values.reduce((sum, opt) => sum + opt.value, 0);
      const optionIds = values.map(opt => opt.id);

      onUpdate({
        id,
        checked: true,
        optionIds,
        value: totalValue
      });
    },
    [onUpdate, id]
  );

  const handleOptionChanged = useCallback(
    e => {
      const { checked } = e.target;

      // For checkbox display mode WITH options enabled, don't add a default value
      // because the user needs to explicitly select options first.
      // If withOptions is false, we should use the budget line's fixed value instead.
      if (
        displayMode === DISPLAY_MODE_CHECK_BOXES &&
        budgetLine.withOptions &&
        maximumSelectableOptions !== 1
      ) {
        onUpdate({ id, checked, value: 0, optionIds: [] });
        return;
      }

      // For gauge and radio display modes, add a default selected value
      // so it always has a value and can't be submitted without one, avoiding validation
      // errors.
      const defaultOptionIds =
        currentOptionIds.length > 0
          ? currentOptionIds
          : [budgetLine.budgetLineOptions?.[0]?.id].filter(Boolean);

      // Find the selected options to calculate the total value
      const selectedOptions = budgetLine.budgetLineOptions?.filter(option =>
        defaultOptionIds.includes(option.id)
      );

      // Calculate the total value adding the selected options values or fallback to the
      // budget line value if there are no available budgetLineOptions
      const totalValue = budgetLine.withOptions
        ? selectedOptions.reduce((sum, opt) => sum + opt.value, 0)
        : budgetLine.value;

      onUpdate({ id, checked, value: totalValue, optionIds: defaultOptionIds });
    },
    [
      displayMode,
      maximumSelectableOptions,
      currentOptionIds,
      budgetLine.budgetLineOptions,
      budgetLine.withOptions,
      budgetLine.value,
      onUpdate,
      id
    ]
  );

  // When the line is checked or unchecked, we need to notify the parent collapse that its
  // content height has changed so it can recalculate it.
  useEffect(() => {
    dispatchEvent(containerRef.current, "newContentLoaded");
  }, [isLineChecked]);

  const containerClassName = classNames("budget-line-container", {
    "budget-line-selected": isLineChecked
  });

  const optionLabelClassName = classNames("d-flex", "w-100", {
    "justify-content-between": isDesktop,
    "flex-column": isNonDesktop
  });

  const budgetAmountClassName = classNames("option-label-value", {
    "margin-left-sm": isDesktop,
    "margin-top-xxs": isNonDesktop
  });

  return (
    <div className={containerClassName} ref={containerRef}>
      <div className="d-flex budget-line-option-container">
        <OptionInput
          type="checkbox"
          option={budgetLine}
          value={id}
          name={fieldName}
          onChange={handleOptionChanged}
          checked={isLineChecked}
          containerClassName="flex-grow-1"
        >
          <span className={optionLabelClassName}>
            <span className="option-label-name">
              {name}

              {budgetLine.description && isDesktop && (
                <Tooltip content={showDescriptionLabel}>
                  <Button
                    type="button"
                    className="btn btn-link padding-x-xs"
                    iconClassName="fas fa-info-circle text-muted"
                    onClick={toggleDescription}
                    aria-expanded={isDescriptionExpanded}
                  />
                </Tooltip>
              )}
            </span>

            <BudgetAmount
              amount={currentValue}
              className={budgetAmountClassName}
              unit={unit}
            />
          </span>
        </OptionInput>

        {budgetLine.description && isNonDesktop && (
          <Button
            type="button"
            className="btn btn-link padding-x-sm padding-y-xs"
            iconClassName="fas fa-info-circle text-muted"
            onClick={toggleDescription}
            aria-expanded={isDescriptionExpanded}
            aria-label={showDescriptionLabel}
          />
        )}
      </div>

      {budgetLine.description && (
        <CollapseContent collapseId={collapseId} isExpanded={isDescriptionExpanded}>
          <RichText
            content={budgetLine.description}
            className="budget-line-description padding-sm"
          />
        </CollapseContent>
      )}

      {withOptions && isLineChecked && (
        <div className="budget-line-options">
          {displayMode === DISPLAY_MODE_CHECK_BOXES ? (
            <BudgetLineCheckboxInput
              budgetLine={{
                ...budgetLine,
                value: currentValue
              }}
              unit={unit}
              currentOptionIds={currentOptionIds}
              onValueChange={handleOptionUpdate}
            />
          ) : (
            <GaugeSlider
              budgetLine={{
                ...budgetLine,
                value: currentValue
              }}
              currentStepId={currentOptionIds[0]}
              onValueChange={handleOptionUpdate}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default BudgetLineInput;

const intlMessages = defineMessages({
  showDescription: {
    id: "budgetLine.showDescription",
    defaultMessage: "Cliquer pour voir plus d'info"
  }
});
